import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatNumber } from '../../lib/format';
import { IconButton } from './Button';

export type PaginationProps = {
  /** 1-based current page. */
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  /** When provided, shows a page-size picker. Changing size should reset to page 1. */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  className?: string;
};

/** "1–50 of 1,234" with previous/next controls. */
export function Pagination({
  page,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
  className,
}: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <nav aria-label="Pagination" className={clsx('flex items-center justify-between gap-3 text-xs text-slate-500', className)}>
      <div className="flex items-center gap-3">
        <span className="tabular">
          {formatNumber(from)}–{formatNumber(to)} of {formatNumber(total)}
        </span>
        {onPageSizeChange && (
          <label className="hidden items-center gap-1.5 sm:flex">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-7 rounded-md border border-slate-300 bg-surface px-1.5 text-xs text-slate-700 focus:border-ember-500 focus:outline-none"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center gap-1">
        <span className="mr-1 tabular">
          Page {formatNumber(Math.min(page, pageCount))} of {formatNumber(pageCount)}
        </span>
        <IconButton icon={ChevronLeft} label="Previous page" size="sm" variant="secondary" disabled={page <= 1} onClick={() => onChange(page - 1)} />
        <IconButton icon={ChevronRight} label="Next page" size="sm" variant="secondary" disabled={page >= pageCount} onClick={() => onChange(page + 1)} />
      </div>
    </nav>
  );
}
